import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { EdgeMailAppearancePreferences } from '@/types/appearance';
import { LEX_THEME_CUSTOM, getLexTheme } from '@/types/themeRegistry';
import { applyThemeModeToDocument } from '@/utils/themeMode';
import {
  DEFAULT_APP_PREFERENCES,
  type AppPreferences,
  type AppPreferencesUpdate,
  mergeEmailPreferences,
} from '@/types/appPreferences';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  type NotificationPreferences,
} from '@/features/edgemail-client/store/settingsStore';

export interface UserPreferencesPayload {
  appearancePreferences?: Partial<EdgeMailAppearancePreferences>;
  notificationPreferences?: Partial<NotificationPreferences>;
  notificationDefaults?: Partial<NotificationPreferences>;
  notificationOverrides?: Partial<NotificationPreferences>;
  notificationPreferencesInherited?: boolean;
  appPreferences?: AppPreferencesUpdate;
}

export interface UserPreferencesState {
  appearancePreferences: EdgeMailAppearancePreferences;
  notificationPreferences: NotificationPreferences;
  notificationDefaults: NotificationPreferences;
  notificationOverrides: Partial<NotificationPreferences>;
  notificationPreferencesInherited: boolean;
  appPreferences: AppPreferences;
  hasHydrated: boolean;
  setAppearancePreferences: (prefs: Partial<EdgeMailAppearancePreferences>) => void;
  applyLexTheme: (themeId: string) => void;
  setNotificationPreferences: (prefs: Partial<NotificationPreferences>) => void;
  useFirmNotificationDefaults: () => void;
  setAppPreferences: (prefs: AppPreferencesUpdate) => void;
  setAllPreferences: (prefs: Partial<UserPreferencesPayload>) => void;
  markHydrated: () => void;
}

const DEFAULT_APPEARANCE_PREFERENCES: EdgeMailAppearancePreferences = {
  // Product default is Light (David, 2026-09-03). Must match index.html's
  // pre-paint script and the backend EdgeMailUserPreferencesService default.
  themeMode: 'light',
  accentColor: 'oxblood',
  accentCustomHex: null,
  neutralBase: 'warm',
  lexTheme: 'oxblood',
  visionMode: 'none',
  wallpaper: 'night-mountain',
  wallpaperCustom: null,
  presetTheme: 'classic',
  wallpaperDim: 0.2,
  wallpaperBlur: 0,
  density: 'cozy',
  fontScale: 1,
  contrastMode: 'standard',
  fontCustomColor: null,
  fontBodyColor: null,
  fontDefaultColor: null,
  fontDefaultBodyColor: null,
  fontCustomColorForce: false,
  fontBodyColorForce: false,
  appearanceDefaults: null,
  mailTextPrimaryColor: null,
  mailTextSecondaryColor: null,
  mailTextDefaultPrimaryColor: null,
  mailTextDefaultSecondaryColor: null,
  mailTextPrimaryColorForce: false,
  mailTextSecondaryColorForce: false,
  mailSidebarTextColor: null,
  mailSidebarTextDefaultColor: null,
  mailSidebarTextColorForce: false,
};

const extractNotificationOverrides = (
  current: NotificationPreferences,
  defaults: NotificationPreferences,
): Partial<NotificationPreferences> => {
  const overrides: Partial<NotificationPreferences> = {};
  (Object.keys(defaults) as (keyof NotificationPreferences)[]).forEach((key) => {
    if (current[key] !== defaults[key]) {
      (overrides as Record<string, NotificationPreferences[keyof NotificationPreferences]>)[key] =
        current[key];
    }
  });
  return overrides;
};

const mergeAppPreferences = (
  base: AppPreferences,
  updates?: AppPreferencesUpdate,
): AppPreferences => ({
  matters: {
    ...base.matters,
    ...(updates?.matters || {}),
  },
  documents: {
    ...base.documents,
    ...(updates?.documents || {}),
  },
  clients: {
    ...base.clients,
    ...(updates?.clients || {}),
  },
  tasks: {
    ...base.tasks,
    ...(updates?.tasks || {}),
  },
  calendar: {
    ...base.calendar,
    ...(updates?.calendar || {}),
  },
  lex: {
    ...base.lex,
    ...(updates?.lex || {}),
  },
  email: mergeEmailPreferences(base.email, updates?.email),
});

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set) => ({
      appearancePreferences: DEFAULT_APPEARANCE_PREFERENCES,
      notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
      notificationDefaults: DEFAULT_NOTIFICATION_PREFERENCES,
      notificationOverrides: {},
      notificationPreferencesInherited: true,
      appPreferences: DEFAULT_APP_PREFERENCES,
      hasHydrated: false,

      setAppearancePreferences: (prefs) => {
        if (prefs.themeMode) {
          applyThemeModeToDocument(prefs.themeMode);
        }

        // Directly changing a theme seed (accent / surface palette) detaches
        // the active Lex theme preset — the user is now driving the Custom
        // controls. Callers that apply a whole theme go through applyLexTheme
        // (or pass lexTheme explicitly) and are unaffected.
        const touchesThemeSeed =
          prefs.lexTheme === undefined &&
          (prefs.accentColor !== undefined ||
            prefs.accentCustomHex !== undefined ||
            prefs.neutralBase !== undefined);

        set((state) => ({
          appearancePreferences: {
            ...state.appearancePreferences,
            ...prefs,
            ...(touchesThemeSeed ? { lexTheme: LEX_THEME_CUSTOM } : {}),
          },
        }));
      },

      applyLexTheme: (themeId) => {
        const theme = getLexTheme(themeId);
        set((state) => {
          if (!theme) {
            return {
              appearancePreferences: {
                ...state.appearancePreferences,
                lexTheme: LEX_THEME_CUSTOM,
              },
            };
          }
          return {
            appearancePreferences: {
              ...state.appearancePreferences,
              lexTheme: theme.id,
              visionMode: theme.visionMode,
              accentColor: theme.accentColor,
              ...(theme.accentColor === 'custom' && theme.accentCustomHex
                ? { accentCustomHex: theme.accentCustomHex }
                : {}),
              ...(theme.neutralBase ? { neutralBase: theme.neutralBase } : {}),
            },
          };
        });
      },

      setNotificationPreferences: (prefs) =>
        set((state) => {
          const notificationPreferences = {
            ...state.notificationPreferences,
            ...prefs,
          };
          const notificationOverrides = extractNotificationOverrides(
            notificationPreferences,
            state.notificationDefaults,
          );
          return {
            notificationPreferences,
            notificationOverrides,
            notificationPreferencesInherited: Object.keys(notificationOverrides).length === 0,
          };
        }),

      useFirmNotificationDefaults: () =>
        set((state) => ({
          notificationPreferences: { ...state.notificationDefaults },
          notificationOverrides: {},
          notificationPreferencesInherited: true,
        })),

      setAppPreferences: (prefs) =>
        set((state) => ({
          appPreferences: mergeAppPreferences(state.appPreferences, prefs),
        })),

      setAllPreferences: (prefs) => {
        if (prefs.appearancePreferences?.themeMode) {
          applyThemeModeToDocument(prefs.appearancePreferences.themeMode);
        }

        set((state) => {
          const hasNotificationPayload =
            prefs.notificationPreferences !== undefined ||
            prefs.notificationDefaults !== undefined ||
            prefs.notificationOverrides !== undefined ||
            prefs.notificationPreferencesInherited !== undefined;
          const notificationDefaults = hasNotificationPayload
            ? {
                ...DEFAULT_NOTIFICATION_PREFERENCES,
                ...(prefs.notificationDefaults || {}),
              }
            : state.notificationDefaults;
          const notificationPreferences = hasNotificationPayload
            ? {
                ...notificationDefaults,
                ...(prefs.notificationPreferences || {}),
              }
            : state.notificationPreferences;
          const notificationOverrides = hasNotificationPayload
            ? prefs.notificationOverrides || extractNotificationOverrides(
                notificationPreferences,
                notificationDefaults,
              )
            : state.notificationOverrides;

          return {
            appearancePreferences: {
              ...DEFAULT_APPEARANCE_PREFERENCES,
              ...state.appearancePreferences,
              ...(prefs.appearancePreferences || {}),
            },
            notificationPreferences,
            notificationDefaults,
            notificationOverrides,
            notificationPreferencesInherited: hasNotificationPayload
              ? prefs.notificationPreferencesInherited ?? Object.keys(notificationOverrides).length === 0
              : state.notificationPreferencesInherited,
            appPreferences: mergeAppPreferences(
              mergeAppPreferences(DEFAULT_APP_PREFERENCES, state.appPreferences),
              prefs.appPreferences,
            ),
          };
        });
      },

      markHydrated: () => set({ hasHydrated: true }),
    }),
    {
      name: 'edgelex-user-preferences',
      partialize: (state) => ({
        appearancePreferences: state.appearancePreferences,
        notificationPreferences: state.notificationPreferences,
        notificationDefaults: state.notificationDefaults,
        notificationOverrides: state.notificationOverrides,
        notificationPreferencesInherited: state.notificationPreferencesInherited,
        appPreferences: state.appPreferences,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<UserPreferencesState> | undefined;
        return {
          ...currentState,
          appearancePreferences: {
            ...DEFAULT_APPEARANCE_PREFERENCES,
            ...(persisted?.appearancePreferences || {}),
          },
          notificationPreferences: {
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...(persisted?.notificationPreferences || {}),
          },
          notificationDefaults: {
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...(persisted?.notificationDefaults || {}),
          },
          notificationOverrides: persisted?.notificationOverrides || {},
          notificationPreferencesInherited: persisted?.notificationPreferencesInherited ?? true,
          appPreferences: mergeAppPreferences(
            DEFAULT_APP_PREFERENCES,
            persisted?.appPreferences,
          ),
        };
      },
    }
  )
);

export const getDefaultUserPreferences = (): UserPreferencesPayload => ({
  appearancePreferences: DEFAULT_APPEARANCE_PREFERENCES,
  notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
  notificationDefaults: DEFAULT_NOTIFICATION_PREFERENCES,
  notificationOverrides: {},
  notificationPreferencesInherited: true,
  appPreferences: DEFAULT_APP_PREFERENCES,
});
